import { defineStore } from "pinia";
import { api } from "@/helpers/api.js";

import { useLocationsStore } from "@/store/locations.js";
import { useSportTypesStore } from "@/store/sportTypes.js";
import { getTeamBySlug } from "@/composables/teams";

const TODAY = new Date().toISOString().substr(0, 10);

export const useEventsStore = defineStore("events", {
  state: () => ({
    events: null, // Use null instead of [] to track if search has been performed
    bookingorder_filter_events: [],
    pager: {
      page: 1,
      page_size: 25,
      total_size: 0,
      total_pages: 0,
    },
    fixed: {},
    query: {},
    upcoming: [],
    popular: [],
    newEvents: [],
    last: "",
    loading: false,
    noResults: false,
  }),
  actions: {
    fetchUpcoming(brandconfig) {
      this.loading = true;
      const params = new URLSearchParams(brandconfig.quicklist.params || {});
      params.append("date_stop", "ge:" + TODAY);
      params.append("tickets_available", "ge:1");
      params.append("ticket_price_eur", "ge:1");
      params.append("sorting", "date_stop:asc");
      params.append("event_status", "in:[nosale,notstarted,postponed]");
      //params.append('sport_type', 'motorsport')
      params.append("page_size", 5);
      api
        .cache("/events?" + params)
        .then((res) => (this.upcoming = res.data.events))
        .finally(() => {
          this.loading = false;
        });
    },
    fetchPopular(brandconfig) {
      this.loading = true;
      const params = new URLSearchParams(brandconfig.quicklist.params || {});
      params.append("date_stop", "ge:" + TODAY);
      params.append("tickets_available", "ge:1");
      params.append("ticket_price_eur", "ge:1");
      params.append("popular_events", true);
      params.append("event_status", "in:[nosale,notstarted,postponed]");
      //params.append('sport_type', 'motorsport')
      params.append("page_size", 5);
      api
        .cache("/events?" + params)
        .then((res) => (this.popular = res.data.events))
        .finally(() => {
          this.loading = false;
        });
    },
    fetchNewEvents(brandconfig) {
      this.loading = true;
      const params = new URLSearchParams(brandconfig.quicklist.params || {});
      params.append("date_stop", "ge:" + TODAY);
      params.append("tickets_available", "ge:1");
      params.append("ticket_price_eur", "ge:1");
      params.append("event_status", "in:[nosale,notstarted,postponed]");
      params.append("sorting", "created:desc");
      params.append("page_size", 5);
      api
        .cache("/events?" + params)
        .then((res) => (this.newEvents = res.data.events))
        .finally(() => {
          this.loading = false;
        });
    },
    //fetch() {
    //console.warn('eventsStore.fetch: DEPRECATED!')
    //},
    async search() {
      this.loading = true;
      this.noResults = false; // Reset noResults when starting a new search
      const locationsStore = useLocationsStore();
      const sportTypesStore = useSportTypesStore();

      const query = { ...this.query, ...this.fixed };
      const pager = this.pager;

      const key = JSON.stringify({ query, pager });

      if (this.last === key) {
        this.loading = false; // Reset loading if we're returning early
        return;
      } else {
        this.last = key;
      }

      const params = {
        page: pager.page,
        page_size: pager.page_size,
        sorting: "date_stop:asc",
        event_status: "in:[nosale,notstarted,postponed,soldout]",
        ...query,
      };

      if (query.tickets_available > "") {
        params.tickets_available = "ge:" + query.tickets_available;
      } else {
        params.tickets_available = "ge:1";
      }

      if (query.sport_type) {
        if (query.sport_type === "motorsport") query.sport_type = "in:[formula1,motorsport,motogp]"; // temp fix
        params.sport_type = query.sport_type === "football" ? "soccer" : query.sport_type;
      }

      delete params.country;
      if (Array.isArray(query.country)) {
        const countries = [];
        query.country.forEach((t) => {
          countries.push(t);
          params.country = `in:[${countries}]`;
        });
      } else if (query.country) {
        params.country = query.country;
      }

      delete params.city;
      if (Array.isArray(query.city)) {
        const cities = [];
        query.city.forEach((t) => {
          // console.log(t)
          cities.push(t);
          params.city = `in:[${cities}]`;
        });
      } else if (query.city) {
        console.log(query.city);
        params.city = query.city;
      }

      delete params.team;
      if (Array.isArray(query.team)) {
        const teams = [];
        query.team.forEach((t) => {
          console.log(t);
          teams.push(t);
          params.team_id = `in:[${teams}]`;
        });
      } else if (query.team) {
        const team = await getTeamBySlug(query.team);
        params.team_id = team?.teamId;
      }

      delete params.tournament;
      if (Array.isArray(query.tournament)) {
        await sportTypesStore.ready;
        const tournaments = [];
        query.tournament.forEach((t) => {
          const tournament = sportTypesStore.getTournament(t);
          if (tournament) {
            tournaments.push(tournament.tournament_id);
          }
          params.tournament_id = `in:[${tournaments}]`;
        });
      } else if (query.tournament) {
        await sportTypesStore.ready;
        const tournament = sportTypesStore.getTournament(query.tournament);
        if (tournament) {
          params.tournament_id = tournament.tournament_id;
        }
      }

      delete params.venue;
      if (query.venue) {
        await locationsStore.ready;
        const venue = locationsStore.getVenue(query.venue);
        params.venue_id = venue.venue_id;
      }

      delete params.date_start;
      delete params.date_stop;
      if (query.date_stop) {
        params.date_stop = "ge:" + query.date_stop;
      } else {
        params.date_stop = "ge:" + TODAY;
      }
      const min = (query.min_ticket_price_eur | 0) * 100;
      const max = (query.max_ticket_price_eur | 0) * 100;
      delete params.min_ticket_price_eur;
      delete params.max_ticket_price_eur;
      if (min) {
        params.ticket_price_eur = "ge:" + min;
      }
      if (max) {
        params.ticket_price_eur = "between:[" + min + "," + max + "]";
      }

      // console.log(params)

      api
        .cache("/events?" + new URLSearchParams(params))
        .then((res) => {
          // console.log(res.data)
          const { pagination, events } = res.data;
          this.pager.total_size = pagination.total_size;
          this.pager.total_pages = Math.ceil(pagination.total_size / this.pager.page_size);
          this.events = events;
          // Set noResults based on whether we have events
          this.noResults = events.length === 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    clear() {
      for (const k in this.query) {
        delete this.query[k];
      }
    },
    reset(query) {
      this.clear();
      Object.assign(this.query, query);
    },
    get_popular(query, next) {
      this.loading = true;
      const params = new URLSearchParams({
        date_stop: "ge:" + TODAY,
        sorting: "date_stop:asc",
        page_size: 5,
        popular_events: true,
        tickets_available: "ge:1",
        event_status: "in:[nosale,notstarted,postponed]",
        ...query,
      });
      api
        .cache("/events?" + params)
        .then((res) => res.data.events)
        .then(next)
        .finally(() => {
          this.loading = false;
        });
    },
    get_upcoming(query, next) {
      this.loading = true;
      const params = new URLSearchParams({
        date_stop: "ge:" + TODAY,
        sorting: "date_stop:asc",
        page_size: 5,
        popular_events: false,
        tickets_available: "ge:1",
        event_status: "in:[nosale,notstarted,postponed]",
        ...query,
      });
      api
        .cache("/events?" + params)
        .then((res) => res.data.events)
        .then(next)
        .finally(() => {
          this.loading = false;
        });
    },

    fetchEventsByIds(unique_event_ids) {
      let getUri = "events";
      const eventsChunks = [];
      const events = [];
      const chunkSize = 50; // 50 * 36 characters is 1800 so were still under max url length of 2000

      for (let i = 0; i < unique_event_ids.length; i += chunkSize) {
        let params = new URLSearchParams();
        const chunk = unique_event_ids.slice(i, i + chunkSize);
        const unique_event_id_chunk = { event_ids: chunk };
        params.append("event_id", `in:${unique_event_id_chunk.event_ids}`);
        eventsChunks.push(
          api.get(`${getUri}?` + params).then((response) => {
            events.push(...response.data.events);
          })
        );
      }
      Promise.all(eventsChunks).then(() => {
        this.bookingorder_filter_events = events;
      });
    },

    getEvent(id) {
      const event = this.bookingorder_filter_events.find((event) => event.event_id === id);
      if (event) {
        return event;
      }
    },

    subscribeToNewsletter(email, tag) {
      const payload = {
        email: email,
        tag: tag,
      };
      return api
        .post("/newsletters/subscribe", payload)
        .then((response) => {
          return response.data;
        })
        .catch((error) => {
          throw error;
        });
    },
    fetchEventsByCountryAvailability(payload) {
      const params = new URLSearchParams(payload);
      params.append("page_size", 9999);
      return api.get("/events?" + params).then((res) => {
        return res.data.events;
      });
    },
  },
});
